import type { ReactNode } from "react";
import { ChevronLeftIcon } from "./icons";
import { UiLink } from "./link";
import { cn, focusRing } from "./utils";

// Screen title in Newsreader with actions on the right, as on Chats and Calls.
export function PageHeader({
  title,
  actions,
  size = "lg",
  className,
}: {
  title: ReactNode;
  actions?: ReactNode;
  size?: "md" | "lg";
  className?: string;
}) {
  return (
    <header className={cn("flex flex-none items-center justify-between px-5 pt-[22px] pb-3.5", className)}>
      <h1
        className={cn(
          "m-0 font-serif font-medium tracking-[-0.02em]",
          size === "lg" ? "text-[38px] leading-tight" : "text-[32px] leading-tight",
        )}
      >
        {title}
      </h1>
      {actions && <div className="flex gap-2">{actions}</div>}
    </header>
  );
}

export type TopBarProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  subtitleTone?: "default" | "online";
  back?: { href: string; label: string };
  leading?: ReactNode;
  /** Makes the avatar and title a link, such as to contact info. */
  titleHref?: string;
  onTitleClick?: () => void;
  actions?: ReactNode;
  className?: string;
};

// Header for a conversation or a detail screen.
export function TopBar({
  title,
  subtitle,
  subtitleTone = "default",
  back,
  leading,
  titleHref,
  onTitleClick,
  actions,
  className,
}: TopBarProps) {
  const identity = (
    <>
      {leading}
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-base font-bold min-[900px]:text-[17px]">{title}</span>
        {subtitle && (
          <span
            className={cn(
              "truncate text-[13px]",
              subtitleTone === "online" ? "text-olive-ink" : "text-ink-secondary",
            )}
          >
            {subtitle}
          </span>
        )}
      </span>
    </>
  );
  const identityClasses = cn(
    "flex min-w-0 flex-1 items-center gap-2.5 rounded-field text-left text-ink no-underline",
    focusRing,
  );
  return (
    <header
      className={cn(
        "flex flex-none items-center gap-2 border-b border-line bg-linen pt-3.5 pr-2.5 pb-3 pl-1.5",
        "min-[900px]:gap-3 min-[900px]:px-5 min-[900px]:py-3.5",
        className,
      )}
    >
      {back && (
        <UiLink
          href={back.href}
          aria-label={back.label}
          className={cn(
            "flex h-11 w-11 flex-none items-center justify-center rounded-field text-ink",
            focusRing,
          )}
        >
          <ChevronLeftIcon size={24} strokeWidth={2.2} />
        </UiLink>
      )}
      {titleHref ? (
        <UiLink href={titleHref} className={identityClasses}>
          {identity}
        </UiLink>
      ) : onTitleClick ? (
        <button type="button" onClick={onTitleClick} className={cn(identityClasses, "cursor-pointer")}>
          {identity}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2.5">{identity}</div>
      )}
      {actions && <div className="flex flex-none items-center gap-1 min-[900px]:gap-2">{actions}</div>}
    </header>
  );
}
